import React, { useState, useEffect } from 'react';
import { 
  Ticket, Plus, Edit2, Trash2, Search, Copy, Check, X, 
  Percent, Crown, Calendar, Users, AlertCircle, TrendingUp,
  ChevronDown, ChevronUp, Clock
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import FilterInput from '../components/FilterInput';
import api from '../services/api';
import { toast } from 'sonner';

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [expandedCoupon, setExpandedCoupon] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  
  const [formData, setFormData] = useState({
    code: '',
    coupon_type: 'percentage',
    discount_percent: 50,
    duration_months: 3,
    max_uses: 100,
    expires_at: '',
    applicable_plan_ids: [],
    description: ''
  });

  useEffect(() => {
    fetchData();
  }, [showInactive]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [couponsRes, plansRes, statsRes] = await Promise.all([
        api.get(`/coupons?include_inactive=${showInactive}`),
        api.get('/main-admin/membership-plans'),
        api.get('/coupons/stats/overview')
      ]);
      setCoupons(couponsRes.data);
      setPlans(plansRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to fetch coupons');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        code: formData.code || undefined, // Let backend generate if empty
        expires_at: new Date(formData.expires_at).toISOString(),
        applicable_plan_ids: formData.applicable_plan_ids.length > 0 ? formData.applicable_plan_ids : []
      };

      if (editingCoupon) {
        await api.put(`/coupons/${editingCoupon.id}`, payload);
        toast.success('Coupon updated successfully');
      } else {
        await api.post('/coupons', payload);
        toast.success('Coupon created successfully');
      }

      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save coupon');
    }
  };

  const handleDelete = async (couponId) => {
    if (!window.confirm('Are you sure you want to deactivate this coupon?')) return;
    try {
      await api.delete(`/coupons/${couponId}`);
      toast.success('Coupon deactivated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate coupon');
    }
  };

  const openEditModal = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      coupon_type: coupon.coupon_type,
      discount_percent: coupon.discount_percent || 50,
      duration_months: coupon.duration_months || 3,
      max_uses: coupon.max_uses,
      expires_at: coupon.expires_at ? coupon.expires_at.split('T')[0] : '',
      applicable_plan_ids: coupon.applicable_plan_ids || [],
      description: coupon.description || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingCoupon(null);
    // Set default expiration to 30 days from now
    const defaultExpiry = new Date();
    defaultExpiry.setDate(defaultExpiry.getDate() + 30);
    
    setFormData({
      code: '',
      coupon_type: 'percentage',
      discount_percent: 50,
      duration_months: 3,
      max_uses: 100,
      expires_at: defaultExpiry.toISOString().split('T')[0],
      applicable_plan_ids: [],
      description: ''
    });
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
    toast.success('Coupon code copied!');
  };

  const filteredCoupons = coupons.filter(coupon => 
    coupon.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (coupon.description && coupon.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const isExpired = (expiresAt) => {
    return expiresAt && new Date(expiresAt) < new Date();
  };

  const getPlanNames = (planIds) => {
    if (!planIds || planIds.length === 0) return 'All Plans';
    return planIds.map(id => {
      const plan = plans.find(p => p.id === id);
      return plan ? plan.name : id;
    }).join(', ');
  };

  return (
    <Sidebar>
      <div className="p-4 lg:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Coupon Codes</h1>
              <p className="text-gray-600 text-sm">Manage discount codes for memberships</p>
            </div>
            <button
              onClick={() => { resetForm(); setShowModal(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
              data-testid="create-coupon-btn"
            >
              <Plus className="w-4 h-4" />
              Create Coupon
            </button>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-2 text-gray-500 mb-1">
                  <Ticket className="w-4 h-4" />
                  <span className="text-xs">Total Coupons</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{stats.total_coupons}</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-2 text-green-600 mb-1">
                  <Check className="w-4 h-4" />
                  <span className="text-xs">Active</span>
                </div>
                <p className="text-2xl font-bold text-green-600">{stats.active_coupons}</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-2 text-blue-600 mb-1">
                  <Users className="w-4 h-4" />
                  <span className="text-xs">Total Uses</span>
                </div>
                <p className="text-2xl font-bold text-blue-600">{stats.total_uses}</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-2 text-purple-600 mb-1">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-xs">Total Savings Given</span>
                </div>
                <p className="text-2xl font-bold text-purple-600">${stats.total_discount_given?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          )}

          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <FilterInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search coupons..."
              className="flex-1"
            />
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="rounded border-gray-300"
              />
              Show inactive
            </label>
          </div>

          {/* Coupons List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : filteredCoupons.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <Ticket className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No coupons found</p>
              <button
                onClick={() => { resetForm(); setShowModal(true); }}
                className="mt-4 text-blue-600 hover:underline text-sm"
              >
                Create your first coupon
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredCoupons.map((coupon) => (
                <div 
                  key={coupon.id} 
                  className={`bg-white rounded-xl border-2 overflow-hidden transition ${
                    !coupon.is_active ? 'border-gray-200 opacity-60' : 
                    isExpired(coupon.expires_at) ? 'border-red-200' : 
                    coupon.remaining_uses <= 5 ? 'border-yellow-200' : 'border-gray-200'
                  }`}
                >
                  {/* Main Row */}
                  <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    {/* Coupon Type Icon */}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      coupon.coupon_type === 'free_lifetime' ? 'bg-purple-100' : 'bg-blue-100'
                    }`}>
                      {coupon.coupon_type === 'free_lifetime' ? (
                        <Crown className="w-5 h-5 text-purple-600" />
                      ) : (
                        <Percent className="w-5 h-5 text-blue-600" />
                      )}
                    </div>

                    {/* Code & Description */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <code className="text-lg font-mono font-bold text-gray-900">{coupon.code}</code>
                        <button
                          onClick={() => copyCode(coupon.code)}
                          className="p-1 hover:bg-gray-100 rounded transition"
                          title="Copy code"
                        >
                          {copiedCode === coupon.code ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                        {!coupon.is_active && (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">Inactive</span>
                        )}
                        {isExpired(coupon.expires_at) && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded-full">Expired</span>
                        )}
                        {coupon.remaining_uses <= 0 && (
                          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">Depleted</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {coupon.coupon_type === 'free_lifetime' ? (
                          <span className="text-purple-600 font-medium">Free Lifetime Membership</span>
                        ) : (
                          <span><span className="font-medium">{coupon.discount_percent}% off</span> for {coupon.duration_months} month(s)</span>
                        )}
                        {coupon.description && <span className="text-gray-400"> • {coupon.description}</span>}
                      </p>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-4 text-sm">
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Uses</p>
                        <p className="font-semibold text-gray-900">{coupon.current_uses}/{coupon.max_uses}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Expires</p>
                        <p className={`font-semibold ${isExpired(coupon.expires_at) ? 'text-red-600' : 'text-gray-900'}`}>
                          {new Date(coupon.expires_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setExpandedCoupon(expandedCoupon === coupon.id ? null : coupon.id)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition"
                        title="Details"
                      >
                        {expandedCoupon === coupon.id ? (
                          <ChevronUp className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        )}
                      </button>
                      <button
                        onClick={() => openEditModal(coupon)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4 text-gray-500" />
                      </button>
                      {coupon.is_active && (
                        <button
                          onClick={() => handleDelete(coupon.id)}
                          className="p-2 hover:bg-red-50 rounded-lg transition"
                          title="Deactivate"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedCoupon === coupon.id && (
                    <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500 text-xs">Applicable Plans</p>
                          <p className="font-medium text-gray-900">{getPlanNames(coupon.applicable_plan_ids)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs">Remaining Uses</p>
                          <p className="font-medium text-gray-900">{coupon.remaining_uses}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs">Total Discount Given</p>
                          <p className="font-medium text-gray-900">${coupon.total_discount_given?.toFixed(2) || '0.00'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs">Created</p>
                          <p className="font-medium text-gray-900">{new Date(coupon.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      
                      {/* Recent Usage */}
                      {coupon.usage_history && coupon.usage_history.length > 0 && (
                        <div className="mt-4">
                          <p className="text-gray-500 text-xs mb-2">Recent Usage</p>
                          <div className="space-y-1">
                            {coupon.usage_history.slice(-5).reverse().map((use, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-xs text-gray-600">
                                <Clock className="w-3 h-3" />
                                <span>{use.plan_name}</span>
                                <span className="text-gray-400">•</span>
                                <span className="text-green-600">-${use.discount_amount?.toFixed(2)}</span>
                                <span className="text-gray-400">•</span>
                                <span>{new Date(use.redeemed_at).toLocaleDateString()}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {/* Coupon Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Coupon Type *</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, coupon_type: 'percentage' })}
                    className={`p-3 rounded-lg border-2 text-left transition ${
                      formData.coupon_type === 'percentage' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Percent className={`w-5 h-5 mb-1 ${formData.coupon_type === 'percentage' ? 'text-blue-600' : 'text-gray-400'}`} />
                    <p className="font-medium text-sm">Percentage Off</p>
                    <p className="text-xs text-gray-500">X% off for Y months</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, coupon_type: 'free_lifetime' })}
                    className={`p-3 rounded-lg border-2 text-left transition ${
                      formData.coupon_type === 'free_lifetime' 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Crown className={`w-5 h-5 mb-1 ${formData.coupon_type === 'free_lifetime' ? 'text-purple-600' : 'text-gray-400'}`} />
                    <p className="font-medium text-sm">Free Lifetime</p>
                    <p className="text-xs text-gray-500">100% free forever</p>
                  </button>
                </div>
              </div>

              {/* Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Coupon Code <span className="text-gray-400 font-normal">(leave empty to auto-generate)</span>
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono uppercase"
                  placeholder="e.g., SUMMER50"
                  data-testid="coupon-code-input"
                />
              </div>

              {/* Percentage Discount Settings */}
              {formData.coupon_type === 'percentage' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Discount % *</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={formData.discount_percent}
                      onChange={(e) => setFormData({ ...formData, discount_percent: parseInt(e.target.value) })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Duration (months) *</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.duration_months}
                      onChange={(e) => setFormData({ ...formData, duration_months: parseInt(e.target.value) })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}

              {/* Usage Limit & Expiration */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Uses *</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.max_uses}
                    onChange={(e) => setFormData({ ...formData, max_uses: parseInt(e.target.value) })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    data-testid="coupon-max-uses-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expires On *</label>
                  <input
                    type="date"
                    value={formData.expires_at}
                    onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                    required
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    data-testid="coupon-expires-input"
                  />
                </div>
              </div>

              {/* Plan Restrictions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Applicable Plans <span className="text-gray-400 font-normal">(leave empty for all plans)</span>
                </label>
                <div className="border border-gray-300 rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                  {plans.length === 0 ? (
                    <p className="text-sm text-gray-500">No plans available</p>
                  ) : (
                    plans.filter(p => p.is_active).map((plan) => (
                      <label key={plan.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={formData.applicable_plan_ids.includes(plan.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ 
                                ...formData, 
                                applicable_plan_ids: [...formData.applicable_plan_ids, plan.id] 
                              });
                            } else {
                              setFormData({ 
                                ...formData, 
                                applicable_plan_ids: formData.applicable_plan_ids.filter(id => id !== plan.id) 
                              });
                            }
                          }}
                          className="rounded border-gray-300"
                        />
                        <span className="text-gray-900">{plan.name}</span>
                        <span className="text-gray-500">(${plan.price}/{plan.billing_cycle})</span>
                      </label>
                    ))
                  )}
                </div>
                {formData.applicable_plan_ids.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    <AlertCircle className="w-3 h-3 inline mr-1" />
                    Coupon will be valid for all plans
                  </p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Internal Notes <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Summer 2026 campaign"
                />
              </div>

              {/* Preview */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <p className="text-xs text-gray-500 mb-1">Preview</p>
                <p className="font-medium text-gray-900">
                  {formData.coupon_type === 'free_lifetime' ? (
                    'Free lifetime membership'
                  ) : (
                    `${formData.discount_percent}% off for the first ${formData.duration_months} month(s)`
                  )}
                </p>
                <p className="text-xs text-gray-500">
                  Max {formData.max_uses} uses • Expires {formData.expires_at ? new Date(formData.expires_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 justify-end pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  data-testid="save-coupon-btn"
                >
                  {editingCoupon ? 'Save Changes' : 'Create Coupon'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Sidebar>
  );
};

export default AdminCoupons;
